import { canvasFromPixels, context2d, makeCanvas, readPixels, type CanvasLike } from './canvas';
import { applyColourMode, enhancePixels, type ColourMode, type Enhancements } from './filters';
import { MAX_OUTPUT_LONG_EDGE, warpPixels, type Corners } from './geometry';

/**
 * The engine's steps on canvases, the same on the page and in the Worker. Each returns a NEW canvas
 * and leaves its input untouched, except `rotate` by 0, which returns the input itself.
 */

/** Straightens the quad `corners` of `source` into a new canvas (perspective correction). */
export function rectify(
  source: CanvasLike,
  corners: Corners,
  maxLongEdge = MAX_OUTPUT_LONG_EDGE,
): CanvasLike {
  return canvasFromPixels(warpPixels(readPixels(source), corners, maxLongEdge));
}

/** Applies the photometric corrections to a copy of `source`. */
export function enhance(
  source: CanvasLike,
  settings: Enhancements,
  mode: ColourMode = 'colour',
): CanvasLike {
  return canvasFromPixels(applyColourMode(enhancePixels(readPixels(source), settings), mode));
}

/**
 * Rotates `source` by `degrees` (in-plane, around its centre), returning a NEW canvas grown to
 * contain the whole rotated image on a white background — no content is clipped. A fine level-up
 * adjustment on top of the corner-based perspective correction; `0` returns the source unchanged.
 */
export function rotate(source: CanvasLike, degrees: number): CanvasLike {
  if (degrees === 0) return source;
  const radians = (degrees * Math.PI) / 180;
  const w = source.width;
  const h = source.height;
  const cos = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));
  const outWidth = Math.ceil(w * cos + h * sin);
  const outHeight = Math.ceil(w * sin + h * cos);
  const out = makeCanvas(outWidth, outHeight);
  const ctx = context2d(out);
  // Resolution 9 (SL-0027): this canvas fill pads the rotated canvas's exposed corners on a
  // detached, off-DOM canvas (never attached to the document), so it cannot inherit a live CSS
  // custom property the way an attached element's style can — a documented, accepted
  // design-system exception, not an unported token.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, outWidth, outHeight);
  ctx.translate(outWidth / 2, outHeight / 2);
  ctx.rotate(radians);
  ctx.drawImage(source, -w / 2, -h / 2);
  return out;
}

/** Rotates by quarter turns, clockwise for positive `turns`; lossless, no resampling. */
export function rotateQuarterTurns(source: CanvasLike, turns: number): CanvasLike {
  const q = ((Math.round(turns) % 4) + 4) % 4;
  if (q === 0) return source;
  const w = source.width;
  const h = source.height;
  const out = q === 2 ? makeCanvas(w, h) : makeCanvas(h, w);
  const ctx = context2d(out);
  ctx.translate(out.width / 2, out.height / 2);
  ctx.rotate((q * Math.PI) / 2);
  ctx.drawImage(source, -w / 2, -h / 2);
  return out;
}

/**
 * A copy of `source` whose long edge is at most `maxLongEdge`, and the factor applied (1 when it
 * already fits, in which case the source itself is returned).
 */
export function scaleToFit(
  source: CanvasLike,
  maxLongEdge: number,
): { readonly canvas: CanvasLike; readonly factor: number } {
  const factor = Math.min(1, maxLongEdge / Math.max(source.width, source.height));
  if (factor === 1) return { canvas: source, factor };
  const out = makeCanvas(
    Math.max(1, Math.round(source.width * factor)),
    Math.max(1, Math.round(source.height * factor)),
  );
  const ctx = context2d(out);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, 0, 0, out.width, out.height);
  return { canvas: out, factor };
}
